<template>
  <div>
    <h1 class="evidencing-title">Evidencing</h1>

    <div class="evidencing-view-selector">
      <a class="no-decoration" @click="handleAllClick">All</a>
      <a class="selected">Groups</a>

      <div class="evidencing-view-btns">
        <div class="groups-search">
          <v-icon>mdi-magnify</v-icon>
          <input
            type="text"
            placeholder="Group/Client name..."
            v-model="tableData['groupSearch']"
          />
        </div>

        <v-btn v-if="isSuperUser" class="create-group-btn" outlined height="32" @click="router.push('/evidencing/create-group/')">
          <img :src="plusIcon" />
        </v-btn>
      </div>
    </div>

    <div v-if="loading[EVIDENCING_GROUP_LIST] || evidencingGroups == null">
      <v-progress-circular indeterminate :size="48" color="#79c61c" />
    </div>

    <div v-else-if="evidencingGroups.length == 0">
      <p>No Evidencing groups found for this organization + year</p>
    </div>

    <div class="evidencing-table-wrapper" v-else>
      <div class="fxn-wrapper">
        <h3>{{ sortedFilteredEvidencingGroups.length }} Groups</h3>

        <div class="configuration-btns"></div>
      </div>

      <div class="evidencing-table">
        <v-simple-table>
          <thead>
            <tr>
              <th
                v-for="head, idx in tableData['headers']"
                :key="head + idx"
                :data-header="head.toLowerCase()"
              >
                <a @click="sortTable(head.toLowerCase())">
                  {{ head }}
                  <div class="evidencing-table-sorters" v-if="idx < tableData['headers'].length-1">
                    <font-awesome-icon
                          :icon="['fas', 'caret-up']"
                          :class="getSortingState(head.toLowerCase(), 0)"
                        />
                        <font-awesome-icon
                          :icon="['fas', 'caret-down']"
                          :class="getSortingState(head.toLowerCase(), 1)"
                        />
                  </div>
                </a>
              </th>
            </tr>
          </thead>
  
          <tbody>
            <tr
              v-for="e in sortedFilteredEvidencingGroups"
              :key="e['id'] + e['name']"
              @click="viewGroup(e['id'])"
              :data-is-row="true"
              :data-group-id="e['id']"
            >
              <td>{{ e['name'] }}</td>
              <td>{{ e['client'] }}</td>
              <td><p>{{ e['crops'] }}</p></td>
              <td class="pre-wrap"><p>{{ e['deadlines'] }}</p></td>
              <td>{{ e['farms'].length }}</td>
              <td>{{ e['uploads'] }}</td>
              <td class="progress-cell">
                <v-progress-linear :value="e['submitted']" color="blue-grey" height="24">
                  <template v-slot:default="{ value }">
                    <strong>{{ Math.min(Math.floor(value), 100) }}%</strong>
                  </template>
                </v-progress-linear>
              </td>
              <td class="progress-cell">
                <v-chip color="deep-purple accent-4" outlined>
                  Needs Validation
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </div>
  </div>
</template>

<script>
import plusIcon from "@/assets/images/plus-icon.svg";
import { Evidencing } from "@/store/modules";
import { mapActions, mapState } from "vuex";
import { EVIDENCING_GROUP_LIST } from "@/constants/evidencing";
import router from "@/router";
import Vue from "vue";

export default {
  name: "EvidencingGroupsOverview",
  components: {},
  data() {
    return {
      hoveredGroupId: null,
      hoveredDims: {
        height: 0,
        right: 0,
        top: 0
      },
      tableData: {
        groupSearch: '',
        headers: [
          "Group Name",
          "Client",
          "Crops",
          "Deadlines",
          "Farms",
          "Uploads",
          "Submitted",
          "Status",
        ],
        sort: {
          key: null,
          state: 0,
        },
      },
      plusIcon,
      EVIDENCING_GROUP_LIST,
      router
    }
  },
  computed: {
    ...mapState({
      year: state => state.Organization.year,
      organization: state => state.Organization.organization,
      user: state => state.User.user,
      evidencingGroups: state => state.Evidencing.evidencingGroupList,
      loading: state => state.Evidencing.loading
    }),
    isSuperUser() {
      if (this.user != null) {
        return this.user['is_superuser'];
      }
      
      return false;
    },
    sortedFilteredEvidencingGroups() {
      if (this.sortedEvidencingGroups != null) {
        if (this.tableData['groupSearch'].length > 0) {
          return this.sortedEvidencingGroups
          .filter(({ name }) => {
            return name.toLowerCase().includes(this.tableData["groupSearch"].toLowerCase())
          })
          .map(({ id, name, uploads, client, farms, crops, due, submitted, status }) => {
            const formattedCrops = crops.join(', ');
            const formattedDueDates = due.join('\n');

            return {
              id,
              name,
              client,
              uploads,
              farms,
              status,
              submitted: submitted * 100,
              crops: formattedCrops,
              deadlines: formattedDueDates,
            }
          });
        }
  
        return this.sortedEvidencingGroups
        .map(({ id, name, uploads, client, farms, crops, due, submitted, status }) => {
          const formattedCrops = crops.join(', ');
          const formattedDueDates = due.join('\n');

          return {
            id,
            name,
            client,
            uploads,
            farms,
            status,
            submitted: submitted * 100,
            crops: formattedCrops,
            deadlines: formattedDueDates,
          }
        });
      }

      return []
    },
    sortedEvidencingGroups() {
      if (this.tableData["sort"]["key"] == null) return this.evidencingGroups

      return this.evidencingGroups.toSorted((a,b) => {
        const sortKey = this.tableData['sort']['key'];
        const sortDir = this.tableData['sort']['state'];

        if (sortKey == 'group name') {
          if (a['name'] != null && b['name'] != null)
            return sortDir ? b['name'].localeCompare(a['name']) : a['name'].localeCompare(b['name'])
        }
        if (sortKey == 'client') {
          if (a['client'] != null && b['client'] != null) {
            return sortDir
              ? b['client'].localeCompare(a['client'])
              : a['client'].localeCompare(b['client']);
          }
        }
        if (sortKey == 'crops') {
          if (a['crops'] != null && a['crops'].length > 0 && b['crops'] != null && b['crops'].length > 0) {
            const aCrops = a['crops'].join(', ');
            const bCrops = b['crops'].join(', ');

            return sortDir
              ? bCrops.localeCompare(aCrops)
              : aCrops.localeCompare(bCrops);
          }
        }
        if (sortKey == 'deadlines') {
          if (a['due'] != null && a['due'].length > 0 && b['due'] != null && b['due'].length > 0) {
            const aDeadlines = a['due'].join(', ');
            const bDeadlines = b['due'].join(', ');

            return sortDir
              ? bDeadlines.localeCompare(aDeadlines)
              : aDeadlines.localeCompare(bDeadlines);
          }
        }
        if (sortKey == 'farms') {
          if (a['farms'] != null && a['farms'].length > 0 && b['farms'] != null && b['farms'].length > 0) {
            const aFarms = a['due'].join(', ');
            const bFarms = b['due'].join(', ');

            return sortDir
              ? bFarms.localeCompare(aFarms)
              : aFarms.localeCompare(bFarms);
          }
        }
        if (sortKey == 'uploads') {
          return sortDir
            ? b['uploads'] - a['uploads']
            : a['uploads'] - b['uploads']
        }
        if (sortKey == 'submitted') {
          return sortDir
            ? b['submitted'] - a['submitted']
            : a['submitted'] - b['submitted']
        }

        return 0
      });
    }
  },
  methods: {
    ...mapActions({
      fetchAllOrgEvidencingData: Evidencing.Actions.fetchAllOrgEvidencingData,
      fetchEvidencingGroupData: Evidencing.Actions.fetchEvidencingGroupData,
      fetchEvidencingByOrgNode: Evidencing.Actions.fetchEvidencingByOrgNode,
      deleteEvidencingGroup: Evidencing.Actions.deleteEvidencingGroup
    }),
    getSortingState(header, state) {
      return this.tableData["sort"]["key"] == header &&
        this.tableData["sort"]["state"] == state
        ? "activated-sort"
        : ""
    },
    sortTable(header) {
      if (this.tableData["sort"]["key"] != null) {
        if (this.tableData["sort"]["key"] == header) {
          if (this.tableData["sort"]["state"] == 0) {
            this.tableData["sort"]["state"] += 1
          } else {
            this.tableData["sort"]["key"] = null
            this.tableData["sort"]["state"] = 0
          }
        } else {
          this.tableData["sort"]["key"] = header
          this.tableData["sort"]["state"] = 0
        }
      } else {
        this.tableData["sort"]["key"] = header
        this.tableData["sort"]["state"] = 0
      }
    },
    handleAllClick() {
      this.fetchAllOrgEvidencingData({ year: this.year, orgNodeId: this.organization.id });
      router.push('/evidencing/view/');
    },
    getGroupCrops(crops) {
      const cropNames = [];

      for (const crop of crops) {
        if (crop != null && crop['name'] != null) {
          if (!cropNames.includes(crop['name'])) {
            cropNames.push(crop['name'])
          }
        }
        else {
          if (!cropNames.includes('Unspecified Crop')) {
            cropNames.push('Unspecified Crop')
          }
        }
      }

      return cropNames
    },
    viewGroup(id) {
      this.fetchEvidencingGroupData({ year: this.year, groupId: id });
      router.push(`/evidencing/dashboard/`);
    },
    editGroup() {
      if (this.hoveredGroupId != null) {
        router.push(`/evidencing/${this.hoveredGroupId}/edit/`);
      }
    },
    validateGroup() {
      if (this.hoveredGroupId != null) {
        router.push(`/evidencing/${this.hoveredGroupId}/validate/`);
      }
    }
  },
  mounted() {
    if (this.organization != null && this.organization['id'] != null && this.year != null) {
      this.fetchEvidencingByOrgNode({ year: this.year, org_node_id: this.organization['id'] })
    }
  },
  watch: {
    organization: {
      handler(curr) {
        if (curr != null && curr['id'] != null) {
          this.fetchEvidencingByOrgNode({ year: this.year, org_node_id: curr['id'] })
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.evidencing-title {
  font-size: 20px;
  line-height: 1.5;
  font-weight: bold;
  margin: 0 0 8px;
  color: #000000;
}
.evidencing-view-selector {
  display: flex;
  justify-content: flex-start;
}
.evidencing-view-selector a {
  margin-right: 8px;
}
.evidencing-view-selector a.selected {
  font-weight: bold;
}
.evidencing-table-wrapper {
  background: #FFFFFF;
  padding: 24px 0 0;
  margin-top: 24px;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
}
.evidencing-table-wrapper .fxn-wrapper {
  margin-bottom: 24px;
  padding: 0 24px;
}
.evidencing-table-wrapper .fxn-wrapper h3 {
  font-size: 16px;
  line-height: 20px;
  color: #000000;
  font-weight: bold;
}
.evidencing-table {
  max-height: 638px;
  overflow-y: scroll;
}

.groups-search {
  width: 100%;
  max-width: 300px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  padding: 4px 12px;
  display: flex;
  flex-wrap: nowrap;
  align-self: flex-end;
  margin-left: auto;
  margin-right: 8px;
}
.groups-search i {
  margin-right: 12px;
}
.groups-search input {
  width: 100%;
}
.evidencing-view-btns {
  display: flex;
  margin-left: auto;
}
.create-group-btn {
  width: 32px;
  padding: 0 !important;
  min-width: unset !important;
  opacity: 0.5;
  margin-left: 6px;
  transition: opacity 0.25s;
}
.create-group-btn:hover {
  opacity: 1;
}
</style>

<!-- table styles -->
<style scoped>
.evidencing-table ::v-deep(table) {
  table-layout: fixed;
}
th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: #757575;
  background: #ffffff;
  text-transform: uppercase;
  height: 43px !important;
}
th[data-header="group name"] {
  width: 250px;
}
th[data-header="actions"] {
  width: 200px;
}
th[data-header="crop(s)"] {
  width: 100px;
}
th[data-header="due"] {
  width: 120px;
}
th[data-header="uploads"] {
  width: 105px;
}
th[data-header="client"],
th[data-header="status"] {
  width: 175px;
}
th[data-header="farms"] {
  width: 95px;
}
th a {
  padding: 12px 0;
  font-size: 12px;
  line-height: 20px;
  color: #000000 !important;
  display: flex;
  width: 100%;
}
th a .evidencing-table-sorters {
  margin-left: auto;
  position: relative;
}
th a .evidencing-table-sorters > svg {
  color: #cbd5e0;
}
th a .evidencing-table-sorters > svg.activated-sort {
  color: #687588;
}
th a .evidencing-table-sorters > svg:first-of-type {
  position: absolute;
  top: 1px;
  right: 0;
}
th a .evidencing-table-sorters > svg:last-of-type {
  position: absolute;
  bottom: 1px;
  right: 0;
}
th,
td {
  font-size: 14px;
  line-height: 20px;
}
td * {
  pointer-events: none;
}
tr {
  position: relative;
}
td {
  /* white-space: pre-wrap; */
  vertical-align: top;
  padding: 12px 16px !important;
}
td > button:not(:last-of-type) {
  margin-right: 8px;
}
td p {
  margin: 0;
  color: #000000;
  line-height: 1.5;
  text-transform: capitalize;
}
tbody tr {
  cursor: pointer;
}
.pre-wrap {
  white-space: pre-wrap;
}
.no-decoration {
  color: rgba(0, 0, 0, 0.5);
  transition: color 0.25s;
}
.no-decoration:hover {
  color: #000000;
}
td.progress-cell {
  vertical-align: middle;
}
.v-progress-linear {
  width: calc(100% - 12px) !important;
  border-radius: 6px;
}
.superuser-hoverable-actions {
  position: absolute;
  right: 0;
}
.hovered-row-actions-wrapper {
  position: fixed;
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
  width: 375px;
  height: var(--box-height, 0px);
  right: var(--box-right, 0px);
  top: var(--box-top, 0px);
  border-bottom-right-radius: 8px;
  z-index: 999;
}
.hovered-row-actions {
  width: 325px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 24px;
  background: #FFFFFF;
  box-shadow: 0 0 30px 30px rgba(255, 255, 255, 1);
}
.hovered-row-actions > button:not(:first-of-type) {
  margin-left: 12px;
}
</style>